import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { GraduationCap, LogOut, BookOpen, ArrowRight, Users, Users2, Library } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import Brand from "../components/Brand";
import RosterTab from "./admin/RosterTab";
import GroupsTab from "./teacher/GroupsTab";
import TeacherResourcesTab from "./teacher/TeacherResourcesTab";

/**
 * Teacher console — read-only roster + per-student analytics + quick links to
 * the regular student content experience. Teachers use the `/staff/*` API
 * surface which mirrors admin's roster & analytics but carries no write
 * permissions.
 */
export default function Teacher() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("roster");

  const handleGoogleSignIn = () => {
    const redirectUrl = window.location.origin + "/auth-callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  if (loading) {
    return <div className="min-h-screen bg-bone flex items-center justify-center text-sage">Loading…</div>;
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-bone flex flex-col items-center justify-center px-4">
        <Brand size="md" linkTo="/" />
        <div className="w-full max-w-md bg-white border border-border rounded-3xl p-8 mt-6">
          <div className="w-12 h-12 rounded-2xl bg-sage flex items-center justify-center text-white mb-5">
            <GraduationCap className="w-6 h-6" strokeWidth={1.7} />
          </div>
          <h1 className="font-display text-2xl font-semibold tracking-tight mb-2">Teacher access</h1>
          <p className="text-sm text-muted-foreground mb-5">Sign in with your teacher Google account.</p>
          <button
            onClick={handleGoogleSignIn}
            data-testid="teacher-google-signin"
            className="w-full inline-flex items-center justify-center gap-3 bg-sage hover:bg-sage-dark text-white font-medium px-5 py-3 rounded-xl transition-all"
          >
            Sign in with Google
          </button>
          <button onClick={() => navigate("/")} className="mt-5 text-xs text-muted-foreground hover:text-sage block mx-auto">← Back to home</button>
        </div>
      </div>
    );
  }

  // Only teacher + admin can use this page.
  if (user.role !== "teacher" && user.role !== "admin") {
    return (
      <div className="min-h-screen bg-bone flex flex-col items-center justify-center px-4">
        <Brand size="md" linkTo="/" />
        <div className="w-full max-w-md bg-white border border-border rounded-3xl p-8 mt-6 text-center">
          <div className="mx-auto w-12 h-12 rounded-2xl bg-terracotta/10 text-terracotta flex items-center justify-center mb-5">
            <GraduationCap className="w-6 h-6" strokeWidth={1.7} />
          </div>
          <h1 className="font-display text-xl font-semibold mb-2">Teacher account required</h1>
          <p className="text-sm text-muted-foreground mb-5">
            You're signed in as <span className="font-medium">{user.email}</span>, which isn't a teacher account.
            Ask the school admin to grant teacher access.
          </p>
          <button onClick={() => navigate("/dashboard")} className="text-sm text-sage hover:underline">
            Go to student dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bone">
      <header className="sticky top-0 z-40 border-b border-border bg-bone/80 backdrop-blur">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 h-14 sm:h-16 flex items-center justify-between gap-2">
          <div className="flex items-center gap-3 min-w-0">
            <Brand size="sm" linkTo="/" />
            <div className="hidden sm:flex items-center gap-2 ps-3 border-s border-border">
              <div className="w-6 h-6 rounded-md bg-sage/10 flex items-center justify-center text-sage">
                <GraduationCap className="w-3.5 h-3.5" strokeWidth={2} />
              </div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold">Teacher Console</div>
            </div>
          </div>
          <div className="flex items-center gap-1 sm:gap-2 shrink-0">
            <Link
              to="/dashboard"
              className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-sage p-2 sm:px-3 sm:py-1.5 rounded-lg hover:bg-white"
              data-testid="teacher-student-view"
              aria-label="Student view"
              title="Student view"
            >
              <BookOpen className="w-4 h-4 shrink-0" />
              <span className="hidden sm:inline whitespace-nowrap">Student view</span>
            </Link>
            <button
              onClick={logout}
              data-testid="teacher-logout"
              aria-label="Sign out"
              title="Sign out"
              className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-sage p-2 sm:px-3 sm:py-1.5 rounded-lg hover:bg-white"
            >
              <LogOut className="w-4 h-4 shrink-0" />
              <span className="hidden sm:inline whitespace-nowrap">Sign out</span>
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-6 sm:py-8">
        <div className="mb-6 sm:mb-8">
          <h1 className="font-display text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight">Teacher Console</h1>
          <p className="text-sm sm:text-base text-muted-foreground mt-1.5">
            Welcome{user.name ? `, ${user.name}` : ""}. Here's how your students are progressing — tap any row to see per-chapter detail.
          </p>
        </div>

        <div className="grid sm:grid-cols-3 gap-3 sm:gap-4 mb-6 sm:mb-8">
          <Link
            to="/dashboard"
            data-testid="teacher-go-student-content"
            className="group bg-white border border-border rounded-2xl p-4 sm:p-5 hover:border-sage/50 hover:shadow-sm transition-all flex items-center gap-4"
          >
            <div className="w-10 h-10 rounded-xl bg-sage/10 text-sage flex items-center justify-center shrink-0">
              <BookOpen className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-display text-base font-semibold">Student mode</div>
              <div className="text-xs text-muted-foreground line-clamp-2">See lessons, flashcards &amp; quizzes exactly as your students do.</div>
            </div>
            <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:text-sage transition-colors shrink-0" />
          </Link>
          <button
            onClick={() => setTab("roster")}
            data-testid="teacher-roster-card"
            className="group bg-white border border-border rounded-2xl p-4 sm:p-5 hover:border-sage/50 hover:shadow-sm transition-all flex items-center gap-4 text-start"
          >
            <div className="w-10 h-10 rounded-xl bg-terracotta/10 text-terracotta flex items-center justify-center shrink-0">
              <Users className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-display text-base font-semibold">Roster &amp; analytics</div>
              <div className="text-xs text-muted-foreground line-clamp-2">Per-student progress, quiz averages, chapter completion.</div>
            </div>
            <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:text-sage transition-colors shrink-0" />
          </button>
          <button
            onClick={() => setTab("resources")}
            data-testid="teacher-resources-card"
            className="group bg-white border border-border rounded-2xl p-4 sm:p-5 hover:border-sage/50 hover:shadow-sm transition-all flex items-center gap-4 text-start"
          >
            <div className="w-10 h-10 rounded-xl bg-sage/10 text-sage flex items-center justify-center shrink-0">
              <Library className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-display text-base font-semibold">Resources</div>
              <div className="text-xs text-muted-foreground line-clamp-2">YouTube videos, PDFs &amp; links you've shared with students.</div>
            </div>
            <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:text-sage transition-colors shrink-0" />
          </button>
        </div>

        {/* Tab switcher — Roster | Groups & Assignments | Resources */}
        <div className="mb-6 inline-flex w-full sm:w-auto items-center gap-1 bg-white border border-border rounded-xl p-1 overflow-x-auto" data-testid="teacher-tabs">
          <TabButton active={tab === "roster"} onClick={() => setTab("roster")} icon={Users} label="Roster" testid="teacher-tab-roster" />
          <TabButton active={tab === "groups"} onClick={() => setTab("groups")} icon={Users2} label="Groups" testid="teacher-tab-groups" />
          <TabButton active={tab === "resources"} onClick={() => setTab("resources")} icon={Library} label="Resources" testid="teacher-tab-resources" />
        </div>

        {tab === "roster" && <RosterTab apiBase="/staff" />}
        {tab === "groups" && <GroupsTab />}
        {tab === "resources" && <TeacherResourcesTab />}
      </div>
    </div>
  );
}

function TabButton({ active, onClick, icon: Icon, label, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`flex-1 sm:flex-initial inline-flex items-center justify-center gap-2 px-3 sm:px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
        active
          ? "bg-sage text-white shadow-sm"
          : "text-muted-foreground hover:text-sage hover:bg-sage/5"
      }`}
    >
      <Icon className="w-4 h-4 shrink-0" />
      <span className="truncate">{label}</span>
    </button>
  );
}
